import { round } from 'lodash';
import React, { FC, useMemo } from 'react';
import {
  TemplateInsightGroupBy,
  TemplateInsightResponses,
} from 'api/template-insight';
import { Filter } from 'components/molecules/filters/types';
import { DateRange } from 'components/organisms/projects-report/common/range-picker/types';
import { useTranslate } from 'hooks/use-translation';
import { useTemplateInsightQuery } from 'queries/template-insight';
import { DelayedEmptyTable, EarlyEmptyTable } from '../empty-state';
import { getTemplateIdFromFilter } from '../utils/common';
import { useTasksViewColumn } from './columns';
import { ShowMoreTable } from './show-more-table';
import { useResponsible } from './use-responsible';

const useTable = <
  T extends
    | TemplateInsightGroupBy.TasksCompletedEarly
    | TemplateInsightGroupBy.TasksCompletedLate,
>({
  templateInsight,
  isTemplateColumnRequired,
  groupBy,
}: {
  templateInsight?: TemplateInsightResponses<T>;
  isTemplateColumnRequired?: boolean;
  groupBy: T;
}) => {
  const convertResponsibilityToUi = useResponsible();

  const tableColumn = useTasksViewColumn({
    isTemplateColumnRequired,
    groupBy,
  });

  return useMemo(() => {
    const dataSource = templateInsight?.templateResponses
      .map((template) => {
        const {
          tasksCompletedEarly,
          tasksCompletedLate,
          duration: plannedDuration,
          completionTime: { averageDuration: average, p50, p90 },
          taskTemplate: { responsible, sourceName: taskName, sourceId: taskId },
          template: { templateName },
        } = template;

        return {
          taskName,
          taskId,
          responsible: convertResponsibilityToUi(responsible),
          template: template.template,
          templateName,
          plannedDuration,
          average: {
            value: average,
            cmpValue: plannedDuration - average,
          },
          p50: {
            value: p50,
            cmpValue: plannedDuration - average,
          },
          p90: {
            value: p90,
            cmpValue: plannedDuration - average,
          },
          percentageTasksCompleted:
            groupBy === TemplateInsightGroupBy.TasksCompletedEarly
              ? round(tasksCompletedEarly)
              : round(tasksCompletedLate),
        };
      })
      .filter((data) => data.percentageTasksCompleted > 50);

    return { tableColumn, dataSource };
  }, [
    convertResponsibilityToUi,
    groupBy,
    templateInsight?.templateResponses,
    tableColumn,
  ]);
};

export const FrequentlyCompletedTable: FC<{
  range: DateRange;
  filter: Filter;
  groupBy:
    | TemplateInsightGroupBy.TasksCompletedEarly
    | TemplateInsightGroupBy.TasksCompletedLate;
}> = ({ range, groupBy, filter }) => {
  const translate = useTranslate();
  const { data: templateInsight, isLoading } = useTemplateInsightQuery({
    groupBy,
    range,
    filter,
  });
  const isTemplateColumnRequired = !getTemplateIdFromFilter(filter);

  const { dataSource, tableColumn } = useTable({
    templateInsight,
    groupBy,
    isTemplateColumnRequired,
  });

  const tableTitle =
    groupBy === TemplateInsightGroupBy.TasksCompletedEarly
      ? translate(
          'reports.template_insight.overview.task.completed_early.table.title',
        )
      : translate(
          'reports.template_insight.overview.task.completed_late.table.title',
        );

  return (
    <ShowMoreTable
      dataSource={dataSource}
      columns={tableColumn}
      titleLabel={tableTitle}
      isLoading={isLoading}
      renderEmpty={
        groupBy === TemplateInsightGroupBy.TasksCompletedEarly
          ? EarlyEmptyTable
          : DelayedEmptyTable
      }
    />
  );
};
